import { FC, memo, useEffect, useCallback, useMemo } from 'react'

import { AutoHide } from '@/components/AutoHide/AutoHide'
import { HtmlWidget } from '@/components/HtmlWidget/HtmlWidget'
import { VideoHeading } from '@/components/VideoHeading/VideoHeading'
import { Box, FocusButtonNode } from '@/components/VideoPlayer/styles'
import { eventCategory } from '@/constants/analytics'
import { focusIds } from '@/constants/focus'
import { icons } from '@/constants/icons'
import { useFindNearestByImgWidth } from '@/hooks/useFindNearestByImgWidth'
import { useToggleFullscreenFocus } from '@/hooks/useToggleFullscreenFocus'
import { useCommonTranslation } from '@/i18n/useCommonTranslation'
import { Keys } from '@/platform/platform'
import useStore from '@/store/useStore'
import { ImageViewerControlsProps } from '@/types/componentsPropsTypes'
import {
  ImageSourceType,
  MediaContentType,
  tileTypes,
} from '@/types/servicesTypes/configTypes'
import { isWebos, sendEventToGTAG } from '@/utils/utils'

import {
  StyledImageViewer,
  StyledControlsContainer,
  ImageViewerControlsContainer,
  HtmlWidgetViewer,
} from './styles'
import { MediaPlayerBackground, BodyText, MediaPlayerWrapper } from '../styles'

type Props = { mediaImage: MediaContentType }

const ImageViewerRaw: FC<Props> = ({ mediaImage }) => {
  const { videoControlsVisible, showVideoControls } = useStore(
    (state) => state.player,
  )
  const setMediaVideo = useStore((state) => state.common.setMediaVideo)

  const imageSource = useFindNearestByImgWidth(
    mediaImage.sources as ImageSourceType[],
    1920,
  )

  const isHtmlWidgetType = mediaImage.isHtmlWidgetType

  useEffect(() => {
    mediaImage &&
      !isHtmlWidgetType &&
      sendEventToGTAG(eventCategory.IMAGE_VIEWED, {
        type: mediaImage.contentType,
        url: imageSource?.url,
        format: imageSource?.format,
        media_name:
          mediaImage.contentType === tileTypes.MEDIA
            ? mediaImage.name
            : undefined,
        ad_name:
          mediaImage.contentType === tileTypes.AD ? mediaImage.name : undefined,
      })
  }, [mediaImage])

  useToggleFullscreenFocus({ mediaContent: mediaImage })

  const onKey = (e: LRUDEvent) => {
    if (!videoControlsVisible) {
      e.stopPropagation()
      e.preventDefault()
      showVideoControls()
    }
  }

  const imageViewerExitHandler = () => setMediaVideo(null)

  return (
    <MediaPlayerBackground $isPopup>
      <MediaPlayerWrapper
        focusId={focusIds.FULLSCREEN_PLAYER}
        onKey={onKey}
        isTrap
      >
        {!isHtmlWidgetType && imageSource?.url && (
          <StyledImageViewer src={imageSource.url} />
        )}
        {isHtmlWidgetType && (
          <HtmlWidgetViewer>
            <HtmlWidget url={mediaImage.htmlWidgetUrl} />
          </HtmlWidgetViewer>
        )}
        <ImageViewerControls
          onExitButton={imageViewerExitHandler}
          title={mediaImage.name}
        />
      </MediaPlayerWrapper>
    </MediaPlayerBackground>
  )
}

export const ImageViewer = memo(ImageViewerRaw)

const ImageViewerControlsRaw: FC<ImageViewerControlsProps> = ({
  onExitButton,
  title,
  focusIdPrefix = '',
}) => {
  const { t } = useCommonTranslation()

  const { videoControlsVisible, hideVideoControls, showVideoControls } =
    useStore((state) => state.player)
  const isWebOs = isWebos()

  const toggleShow = useCallback(
    (next: boolean) => {
      if (next) {
        showVideoControls()
      } else {
        hideVideoControls()
      }
    },
    [showVideoControls, hideVideoControls],
  )

  const options = useMemo(
    () => ({
      onHide: () => hideVideoControls(),
      onShow: () => showVideoControls(),
    }),
    [hideVideoControls, showVideoControls],
  )

  const onExit = (e?: LRUDEvent) => {
    hideVideoControls()
    e?.stopPropagation()
    return onExitButton()
  }

  const keyPressHandler = (e: KeyboardEvent) => {
    if (e.keyCode === Keys.MEDIA_STOP) {
      onExit()
    }
  }

  useEffect(() => {
    if (!isWebOs) {
      return
    }
    window.addEventListener('keydown', keyPressHandler)
    return () => {
      window.removeEventListener('keydown', keyPressHandler)
    }
  }, [])

  return (
    <StyledControlsContainer $inFront={videoControlsVisible}>
      <AutoHide
        show={videoControlsVisible}
        onChange={toggleShow}
        options={options}
      >
        <ImageViewerControlsContainer
          className="ControlsContainer"
          focusId={focusIdPrefix + focusIds.VIDEO_CONTAINER}
          elementType="div"
        >
          <Box $alignItems="center">
            <FocusButtonNode
              elementType="section"
              focusId={focusIdPrefix + 'section_2'}
              onSelected={onExit}
            >
              <img src={icons.navigation.backArrow} alt="" />
            </FocusButtonNode>
            <BodyText>{t('pressBack')}</BodyText>
          </Box>
          <Box $justifyContent="space-between" $alignItems="center">
            <VideoHeading name={title} />
          </Box>
        </ImageViewerControlsContainer>
      </AutoHide>
    </StyledControlsContainer>
  )
}

const ImageViewerControls = memo(ImageViewerControlsRaw)
